import { memo } from 'react';
import PropTypes from 'prop-types';

import { formatValue } from './widgetFormat.js';

/**
 * The value axis down the left of a plot.
 *
 * Each tick is positioned at its own fraction of the height and pulled up half
 * a line, so it sits **on** its gridline rather than beside it. `justify-between`
 * would get the first and last right and leave the middle ones drifting by half
 * a line each — which is exactly where a reader looks to check a value.
 *
 * The column sizes itself to its widest tick through an invisible copy in
 * normal flow. A hard-coded gutter is wrong twice: too narrow for `1,250,000`
 * and too wide for `0–4`, and the plot pays for both.
 *
 * Ticks arrive ascending from 0; they are drawn top-down, because that is the
 * direction the axis runs.
 *
 * @param {object} props
 * @param {number[]} props.ticks Ascending, from 0 — see `niceScale`.
 * @param {object} [props.format] The axis' own `format`, from `y_axes[n]`.
 */
function ChartValueAxis({ ticks, format = null }) {
  if (ticks.length < 2) return null;

  const labels = ticks.map((tick) => formatValue(tick, format, '0'));
  const widest = labels.reduce((a, b) => (b.length > a.length ? b : a), '');

  return (
    <div className="relative shrink-0" aria-hidden>
      {/* Sizer: never seen, but it is what gives the column its width. */}
      <span className="invisible block text-[10px] leading-none">{widest}</span>
      {labels.map((label, index) => (
        <span
          key={ticks[index]}
          className="absolute right-0 -translate-y-1/2 whitespace-nowrap text-[10px] leading-none text-content-muted"
          style={{ top: `${100 - (index / (ticks.length - 1)) * 100}%` }}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

ChartValueAxis.propTypes = {
  ticks: PropTypes.arrayOf(PropTypes.number).isRequired,
  format: PropTypes.object,
};

export default memo(ChartValueAxis);
